import type { ConditionPolicy } from './types';
import { collectOffsetPages } from '../shared/collectOffsetPages';
import type {
	AuthBootstrap,
	AuthSession,
  BusinessProcessEvent,
  BusinessProcessStep,
  Checklist,
  ChecklistTemplateCreateInput,
  ChecklistTemplate,
  ChecklistTemplateVersionInput,
  CollectionResponse,
  DashboardSummary,
  HRDocument,
  InboxItem,
  WorkflowTaskFilters,
  JobChange,
  JobChangeInput,
  StaffingWorker,
  SupervisoryOrganization,
  TimeOffEntry,
  TimeOffCalendarResult,
  TimeOffCalendarQuery,
  TimeOffRequest,
  TimeOffRequestInput,
  TimeOffRequestCalculation,
  TimeOffType,
  RequestableTimeOffType,
  CreatePersonInput,
  CreatedPerson,
  UploadDocumentInput,
  WorkerLifecycleChange,
  WorkerLifecycleDraft,
  WorkerLifecycleDraftUpdate,
  WorkerTransitionInput,
  LifecyclePreview,
  WorkerLifecycleInput,
  WorkerLifecycleType,
  HireReference,
  StaffingChangeSummary,
  WorkerLifecycleCorrectionInput,
  OrganizationChange,
  OrganizationCreateInput,
  OrganizationChangeInput,
  HolidayCalendar,
  TimeOffPlan,
  TimeOffPlanAssignment,
  TimeOffGrantRule,
  WorkdayReference,
  TimeOffBalance,
  EntitlementTransaction,
  AbsenceMaintenanceRun,
  WorkerRewards,
	RewardChange,
	CompanySummary,
	ImpersonationPurposeCode,
	ImpersonationTarget,
	WorkflowActivity,
	WorkflowActivityPage,
	WorkflowTemplate,
	WorkflowTemplateCreateInput,
	WorkflowRequest,
	WorkflowRequestCreateInput,
	WorkflowRequestFilters,
	WorkflowRequestUpdateInput,
	WorkflowAttachment,
	WorkflowReferenceValue,
	WorkflowTemplateUpdateInput,
	WorkflowTemplateVersionInput,
		AdministratorBinding,
		AdministratorBindingInput,
		AdministratorOptions,
		AdministratorRoleInput,
		AdministratorRoleOption,
	} from './types';

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: unknown;

  constructor(status: number, message: string, code?: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type FetchImplementation = typeof fetch;

type InitialAdminSetupRequest = {
  companyName: string;
  rootOrganizationName: string;
  locationName: string;
  legalFamilyName: string;
  legalGivenName: string;
  hireDate: string;
};

type ServerIssuedReferenceCode = {
  code?: never;
};

export type TimeOffTypeCreateRequest = ServerIssuedReferenceCode & {
  descriptor: string;
  category: string;
  approvalRequired: boolean;
  cancellationApprovalRequired: boolean;
  allowedUnit: string;
  managementMode?: 'BALANCE' | 'USAGE_LIMIT';
  dayCountBasis?: 'WORKDAYS' | 'CALENDAR_DAYS';
  overlapPolicy: string;
  paidRatio: string;
  reasonRequired: boolean;
  evidencePolicy: string;
  minimumIncrement: string;
  usageMode: string;
  displayVisibility: string;
  displayColor?: TimeOffType['displayColor'];
  maximumConsecutiveDays?: number | null;
  status: string;
  effectiveFrom: string;
  effectiveTo?: string;
};

export type HolidayCalendarCreateRequest = ServerIssuedReferenceCode & {
  descriptor: string;
  countryCode: string;
  timeZone: string;
  effectiveFrom: string;
  effectiveTo?: string;
};

export type TimeOffPlanCreateRequest = ServerIssuedReferenceCode & ConditionPolicy & {
  descriptor: string;
  timeOffType: WorkdayReference;
  holidayCalendar?: WorkdayReference;
  usageHistoryReviewed?: boolean;
  limitPeriod?: 'MONTHLY' | 'ANNUAL';
  limitQuantity?: string;
  accrualFrequency: string;
  accrualQuantity: string;
  carryOverLimit?: string;
  carryOverExpiryMonths?: number;
  effectiveFrom: string;
  effectiveTo?: string;
  status?: string;
  accrualBasis?: string;
  firstYearMethod?: string;
  annualBaseQuantity?: string;
  firstYearMonthlyQuantity?: string;
  tenureStartYear?: number;
  tenureIntervalYears?: number;
  tenureBonusQuantity?: string;
  maximumAnnualQuantity?: string;
  roundingRule?: string;
  allowAdvance: boolean;
  advanceLimit?: string;
  pauseAccrualDuringLeave: boolean;
};

export type TimeOffGrantRuleCreateRequest = ServerIssuedReferenceCode & {
  descriptor: string;
  triggerType: string;
  quantity: string;
  serviceMonths?: number;
  expiresAfterDays?: number;
  effectiveFrom: string;
  effectiveTo?: string;
};

type ApiClientOptions = {
  tenant: string;
  csrfToken: string;
  workspace?: 'employee' | 'admin';
  fetch?: FetchImplementation;
  onUnauthorized?: () => void;
  onImpersonationEnded?: () => void;
};

const safeMethods = new Set(['GET', 'HEAD', 'OPTIONS']);

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.ok) {
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }

  let message = `요청을 처리하지 못했습니다. (${response.status})`;
  let code: string | undefined;
  let details: unknown;
  try {
    const body = await response.json() as {
      error?: string | { code?: string; message?: string; details?: unknown };
      code?: string;
      details?: unknown;
      blockers?: unknown;
    };
    if (typeof body.error === 'string') {
      message = body.error || message;
      code = body.code;
      details = body.details ?? (body.blockers === undefined ? undefined : { blockers: body.blockers });
    } else {
      message = body.error?.message || message;
      code = body.error?.code || body.code;
      details = body.error?.details ?? body.details ?? (body.blockers === undefined ? undefined : { blockers: body.blockers });
    }
  } catch {
    // JSON 오류 본문이 아닌 경우 상태 코드 기반 메시지를 사용한다.
  }
  throw new ApiError(response.status, message, code, details);
}

export async function getAuthSession(fetchImplementation: FetchImplementation = fetch): Promise<AuthBootstrap> {
  const fetchSession = () => fetchImplementation('/api/auth/session', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  }).then((response) => parseResponse<AuthBootstrap>(response));
  try {
    return await fetchSession();
  } catch (error) {
    // The request that discovers an expired or revoked impersonation is
    // deliberately rejected after ending it and revoking its credential.
    // Retry once so the caller observes 401 and starts a fresh Google login.
    if (error instanceof ApiError && error.code === 'IMPERSONATION_ENDED') return fetchSession();
    throw error;
  }
}

export async function completeInitialAdminSetup(input: InitialAdminSetupRequest, csrfToken: string, fetchImplementation: FetchImplementation = fetch): Promise<AuthSession> {
  const response = await fetchImplementation('/api/auth/initial-admin', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
    body: JSON.stringify(input),
  });
  return parseResponse<AuthSession>(response);
}

export class ApiClient {
  private readonly tenant: string;
  private readonly csrfToken: string;
  private readonly workspace: 'employee' | 'admin';
  private readonly fetchImplementation: FetchImplementation;
  private readonly onUnauthorized?: () => void;
  private readonly onImpersonationEnded?: () => void;

  constructor(options: ApiClientOptions) {
    this.tenant = options.tenant;
    this.csrfToken = options.csrfToken;
    this.workspace = options.workspace ?? 'employee';
    this.fetchImplementation = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.onUnauthorized = options.onUnauthorized;
    this.onImpersonationEnded = options.onImpersonationEnded;
  }

  private tenantPath(path: string): string {
    return path.replace('{tenant}', encodeURIComponent(this.tenant));
  }

  private async fetchResponse(path: string, init: RequestInit = {}): Promise<Response> {
    const method = (init.method ?? 'GET').toUpperCase();
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    // The server validates app management capabilities; this only identifies
    // the workspace initiating the request and never grants permission.
    headers.set('X-Heyday-Workspace', this.workspace);
    const formDataBody = typeof FormData !== 'undefined' && init.body instanceof FormData;
    if (init.body !== undefined && !formDataBody && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    if (!safeMethods.has(method)) headers.set('X-CSRF-Token', this.csrfToken);

    const response = await this.fetchImplementation(path, {
      ...init,
      method,
      headers,
      credentials: 'same-origin',
    });
    if (response.status === 401) this.onUnauthorized?.();
    return response;
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await this.fetchResponse(path, init);
    try {
      return await parseResponse<T>(response);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'IMPERSONATION_ENDED') this.onImpersonationEnded?.();
      throw error;
    }
  }

  requestTenant<T>(path: string, init: RequestInit = {}): Promise<T> {
    return this.request(this.tenantPath(path), init);
  }

  private requestAll<T extends { id: string }>(pathForOffset: (offset: number) => string, pageSize = 100, keyOf?: (item: T) => string): Promise<CollectionResponse<T>> {
    return collectOffsetPages((offset) => this.request<CollectionResponse<T>>(pathForOffset(offset)), pageSize, keyOf);
  }

  logout(): Promise<void> {
    return this.request('/api/auth/logout', { method: 'POST' });
  }

  listImpersonationTargets(search = ''): Promise<CollectionResponse<ImpersonationTarget>> {
    const query = new URLSearchParams({ search: search.trim() });
    return this.request(`/api/auth/impersonation/targets?${query}`);
  }

  startImpersonation(targetIdentityId: string, purposeCode: ImpersonationPurposeCode): Promise<void> {
    return this.request('/api/auth/impersonation', {
      method: 'POST',
      body: JSON.stringify({ targetIdentityId, purposeCode }),
    });
  }

  stopImpersonation(): Promise<void> {
    return this.request('/api/auth/impersonation', { method: 'DELETE' });
  }

  getCompanySummary(): Promise<CompanySummary> {
    return this.request(this.tenantPath('/api/hr/v1/{tenant}/tenantSettings/companySummary'));
  }


  listWorkflowTemplates(includeDraft = false): Promise<CollectionResponse<WorkflowTemplate>> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowTemplates${includeDraft ? '?includeDraft=true' : ''}`));
  }

  getWorkflowTemplate(id: string): Promise<WorkflowTemplate> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowTemplates/${encodeURIComponent(id)}`));
  }

  createWorkflowTemplate(input: WorkflowTemplateCreateInput, idempotencyKey: string = crypto.randomUUID()): Promise<WorkflowTemplate> {
    return this.request(this.tenantPath('/api/hr/v1/{tenant}/workflowTemplates'), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input),
    });
  }

  updateWorkflowTemplate(id: string, input: WorkflowTemplateUpdateInput, idempotencyKey: string = crypto.randomUUID()): Promise<WorkflowTemplate> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowTemplates/${encodeURIComponent(id)}`), {
      method: 'PATCH', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input),
    });
  }

  createWorkflowTemplateVersion(id: string, input: WorkflowTemplateVersionInput, idempotencyKey: string = crypto.randomUUID()): Promise<WorkflowTemplate> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowTemplates/${encodeURIComponent(id)}/versions`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input),
    });
  }

  updateWorkflowTemplateVersion(id: string, input: WorkflowTemplateVersionInput, idempotencyKey: string = crypto.randomUUID()): Promise<WorkflowTemplate> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowTemplateVersions/${encodeURIComponent(id)}`), {
      method: 'PUT', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input),
    });
  }

  publishWorkflowTemplateVersion(versionId: string, expectedRevision: number, idempotencyKey: string = crypto.randomUUID()): Promise<WorkflowTemplate> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowTemplateVersions/${encodeURIComponent(versionId)}/publish`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({ expectedRevision }),
    });
  }

  listWorkflowRequests(filters: WorkflowRequestFilters | boolean = false): Promise<CollectionResponse<WorkflowRequest>> {
    const normalized = typeof filters === 'boolean' ? { all: filters } : filters;
    return this.requestAll((offset) => {
      const query = new URLSearchParams({ limit: '200', offset: String(offset) });
      if (normalized.all) query.set('scope', 'all');
      if (normalized.search) query.set('search', normalized.search);
      if (normalized.templateId) query.set('templateId', normalized.templateId);
      if (normalized.status) query.set('status', normalized.status);
      if (normalized.dateFrom) query.set('dateFrom', normalized.dateFrom);
      if (normalized.dateTo) query.set('dateTo', normalized.dateTo);
      return this.tenantPath(`/api/hr/v1/{tenant}/workflowRequests?${query}`);
    }, 200);
  }

  searchWorkflowRequestReferences(search = ''): Promise<CollectionResponse<WorkflowReferenceValue>> {
    const query = new URLSearchParams();
    if (search.trim()) query.set('search', search.trim());
    const suffix = query.size > 0 ? `?${query}` : '';
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowRequestReferences${suffix}`));
  }

  async exportWorkflowRequests(filters: WorkflowRequestFilters = {}): Promise<Blob> {
    const query = new URLSearchParams();
    if (filters.search) query.set('search', filters.search);
    if (filters.templateId) query.set('templateId', filters.templateId);
    if (filters.status) query.set('status', filters.status);
    if (filters.dateFrom) query.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) query.set('dateTo', filters.dateTo);
    const response = await this.fetchResponse(this.tenantPath(`/api/hr/v1/{tenant}/workflowRequests/export.xlsx?${query}`));
    if (!response.ok) return parseResponse<never>(response);
    return response.blob();
  }

  getWorkflowRequest(id: string): Promise<WorkflowRequest> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowRequests/${encodeURIComponent(id)}`));
  }

  listWorkflowRequestActivities(id: string, cursor = '', limit = 50): Promise<WorkflowActivityPage> {
    const query = new URLSearchParams({ limit: String(limit) });
    if (cursor) query.set('cursor', cursor);
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowRequests/${encodeURIComponent(id)}/activities?${query}`));
  }

  createWorkflowRequestComment(id: string, body: string, idempotencyKey: string): Promise<WorkflowActivity> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowRequests/${encodeURIComponent(id)}/comments`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({ body }),
    });
  }

  createWorkflowRequest(input: WorkflowRequestCreateInput, idempotencyKey: string = crypto.randomUUID()): Promise<WorkflowRequest> {
    return this.request(this.tenantPath('/api/hr/v1/{tenant}/workflowRequests'), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input),
    });
  }

  submitWorkflowRequest(id: string, expectedVersion: number, idempotencyKey: string = crypto.randomUUID()): Promise<WorkflowRequest> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowRequests/${encodeURIComponent(id)}/submit`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({ expectedVersion }),
    });
  }

  updateWorkflowRequestDraft(id: string, input: WorkflowRequestUpdateInput, idempotencyKey: string = crypto.randomUUID()): Promise<WorkflowRequest> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowRequests/${encodeURIComponent(id)}/draft`), {
      method: 'PUT', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input),
    });
  }

  uploadWorkflowRequestAttachment(requestId: string, fieldKey: string, file: File): Promise<WorkflowAttachment> {
    const body = new FormData();
    body.set('file', file);
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowRequests/${encodeURIComponent(requestId)}/fields/${encodeURIComponent(fieldKey)}/files`), {
      method: 'POST', body,
    });
  }

  getWorkflowRequestAttachment(requestId: string, fieldKey: string, fileId: string): Promise<WorkflowAttachment> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowRequests/${encodeURIComponent(requestId)}/fields/${encodeURIComponent(fieldKey)}/files/${encodeURIComponent(fileId)}`));
  }

  async downloadWorkflowRequestAttachment(requestId: string, fieldKey: string, fileId: string, inline = false): Promise<Blob> {
    const query = inline ? '?disposition=inline' : '';
    const response = await this.fetchResponse(this.tenantPath(`/api/hr/v1/{tenant}/workflowRequests/${encodeURIComponent(requestId)}/fields/${encodeURIComponent(fieldKey)}/files/${encodeURIComponent(fileId)}/download${query}`));
    if (!response.ok) return parseResponse<Blob>(response);
    return response.blob();
  }

  deleteWorkflowRequestAttachment(requestId: string, fieldKey: string, fileId: string): Promise<void> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowRequests/${encodeURIComponent(requestId)}/fields/${encodeURIComponent(fieldKey)}/files/${encodeURIComponent(fileId)}`), {
      method: 'DELETE',
    });
  }

  cancelWorkflowRequest(id: string, comment = '', idempotencyKey: string = crypto.randomUUID()): Promise<WorkflowRequest> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowRequests/${encodeURIComponent(id)}/cancel`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({ comment }),
    });
  }

  copyWorkflowRequest(id: string, idempotencyKey: string): Promise<WorkflowRequest> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowRequests/${encodeURIComponent(id)}/copy`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({}),
    });
  }

  listWorkflowTasks(filters: WorkflowTaskFilters = {}): Promise<CollectionResponse<InboxItem>> {
    const query = new URLSearchParams();
    if (filters.limit && filters.limit !== 20) query.set('limit', String(filters.limit));
    if (filters.offset) query.set('offset', String(filters.offset));
    if (filters.view === 'history') query.set('view', filters.view);
    if (filters.search?.trim()) query.set('search', filters.search.trim());
    if (filters.processType) query.set('processType', filters.processType);
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowTasks${query.size ? `?${query}` : ''}`));
  }

  getWorkflowTask(stepId: string): Promise<InboxItem> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowTasks/${encodeURIComponent(stepId)}`));
  }

  requestWorkflowTaskChanges(stepId: string, comment: string, idempotencyKey: string = crypto.randomUUID()): Promise<unknown> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowTasks/${encodeURIComponent(stepId)}/requestChanges`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({ comment }),
    });
  }

  listAdministrators(): Promise<CollectionResponse<AdministratorBinding>> {
    return this.request(this.tenantPath('/api/hr/v1/{tenant}/security/administrators'));
  }

  getAdministratorOptions(): Promise<AdministratorOptions> {
    return this.request(this.tenantPath('/api/hr/v1/{tenant}/security/administratorOptions'));
  }

  createAdministrator(input: AdministratorBindingInput): Promise<AdministratorBinding> {
    return this.request(this.tenantPath('/api/hr/v1/{tenant}/security/administrators'), {
      method: 'POST', body: JSON.stringify(input),
    });
  }

  updateAdministrator(bindingId: string, input: AdministratorBindingInput): Promise<AdministratorBinding> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/security/administrators/${encodeURIComponent(bindingId)}`), {
      method: 'PUT', body: JSON.stringify(input),
    });
  }

  revokeAdministrator(bindingId: string): Promise<void> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/security/administrators/${encodeURIComponent(bindingId)}`), {
      method: 'DELETE',
    });
  }

  createAdministratorRole(input: AdministratorRoleInput): Promise<AdministratorRoleOption> {
    return this.request(this.tenantPath('/api/hr/v1/{tenant}/security/administratorRoles'), {
      method: 'POST', body: JSON.stringify(input),
    });
  }

  updateAdministratorRole(roleCode: string, input: AdministratorRoleInput): Promise<AdministratorRoleOption> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/security/administratorRoles/${encodeURIComponent(roleCode)}`), {
      method: 'PUT', body: JSON.stringify(input),
    });
  }

  approveWorkflowTask(stepId: string, comment = '', idempotencyKey: string = crypto.randomUUID()): Promise<unknown> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowTasks/${encodeURIComponent(stepId)}/approve`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({ comment }),
    });
  }

  rejectWorkflowTask(stepId: string, comment = '', idempotencyKey: string = crypto.randomUUID()): Promise<unknown> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workflowTasks/${encodeURIComponent(stepId)}/reject`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({ comment }),
    });
  }

  listWorkers(search = '', asOf = ''): Promise<CollectionResponse<StaffingWorker>> {
    const query = new URLSearchParams({ limit: '100', offset: '0' });
    if (search.trim()) query.set('search', search.trim());
    if (asOf) query.set('asOf', asOf);
    return this.request(this.tenantPath(`/api/staffing/v7/{tenant}/workers?${query}`));
  }

  listAllWorkers(search = '', asOf = ''): Promise<CollectionResponse<StaffingWorker>> {
    return this.requestAll((offset) => {
      const query = new URLSearchParams({ limit: '100', offset: String(offset) });
      if (search.trim()) query.set('search', search.trim());
      if (asOf) query.set('asOf', asOf);
      return this.tenantPath(`/api/staffing/v7/{tenant}/workers?${query}`);
    });
  }

  getWorker(id: string, asOf = ''): Promise<StaffingWorker> {
    const query = new URLSearchParams();
    if (asOf) query.set('asOf', asOf);
    return this.request(this.tenantPath(`/api/staffing/v7/{tenant}/workers/${encodeURIComponent(id)}${query.size ? `?${query}` : ''}`));
  }

  listSupervisoryOrganizations(asOf = ''): Promise<CollectionResponse<SupervisoryOrganization>> {
    const query = new URLSearchParams({ limit: '100', offset: '0' });
    if (asOf) query.set('asOf', asOf);
    return this.request(this.tenantPath(`/api/staffing/v7/{tenant}/supervisoryOrganizations?${query}`));
  }

  listAllSupervisoryOrganizations(asOf = ''): Promise<CollectionResponse<SupervisoryOrganization>> {
    return this.requestAll((offset) => {
      const query = new URLSearchParams({ limit: '100', offset: String(offset) });
      if (asOf) query.set('asOf', asOf);
      return this.tenantPath(`/api/staffing/v7/{tenant}/supervisoryOrganizations?${query}`);
    });
  }

  listAllOrganizationChanges(): Promise<CollectionResponse<OrganizationChange>> {
    return this.requestAll((offset) => this.tenantPath(`/api/hr/v1/{tenant}/organizationChanges?limit=100&offset=${offset}`));
  }

  listSupervisoryOrganizationMemberPreview(organizationId: string, asOf: string): Promise<CollectionResponse<StaffingWorker>> {
    const query = new URLSearchParams({ asOf, limit: '4', offset: '0' });
    return this.request(this.tenantPath(`/api/staffing/v7/{tenant}/supervisoryOrganizations/${encodeURIComponent(organizationId)}/members?${query}`));
  }

  createSupervisoryOrganization(input: OrganizationCreateInput, idempotencyKey: string = crypto.randomUUID()): Promise<OrganizationChange> {
    return this.request(this.tenantPath('/api/hr/v1/{tenant}/supervisoryOrganizations'), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input),
    });
  }

  createOrganizationChange(organizationId: string, input: OrganizationChangeInput, idempotencyKey: string = crypto.randomUUID()): Promise<OrganizationChange> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/supervisoryOrganizations/${encodeURIComponent(organizationId)}/changes`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input),
    });
  }

  submitOrganizationChange(changeId: string, idempotencyKey: string = crypto.randomUUID()): Promise<OrganizationChange> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/organizationChanges/${encodeURIComponent(changeId)}/submit`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: '{}',
    });
  }

  createJobChange(workerId: string, input: JobChangeInput, idempotencyKey: string = crypto.randomUUID()): Promise<JobChange> {
    return this.request(this.tenantPath(`/api/staffing/v7/{tenant}/workers/${encodeURIComponent(workerId)}/jobChanges`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input),
    });
  }

  submitJobChange(changeId: string, idempotencyKey: string = crypto.randomUUID()): Promise<JobChange> {
    return this.request(this.tenantPath(`/api/staffing/v7/{tenant}/jobChanges/${encodeURIComponent(changeId)}/submit`), { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: '{}' });
  }

  previewWorkerLifecycle(workerId: string, input: WorkerLifecycleInput & { processType: Exclude<WorkerLifecycleType, 'HIRE'> }): Promise<LifecyclePreview> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workers/${encodeURIComponent(workerId)}/workerLifecyclePreview`), { method: 'POST', body: JSON.stringify(input) });
  }

  previewWorkerLifecycleCorrection(eventId: string, input: WorkerLifecycleCorrectionInput): Promise<LifecyclePreview> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workerLifecycleEvents/${encodeURIComponent(eventId)}/correctionPreview`), { method: 'POST', body: JSON.stringify(input) });
  }

  previewWorkerTransition(workerId: string, input: WorkerTransitionInput): Promise<LifecyclePreview> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workers/${encodeURIComponent(workerId)}/relationshipTransitions/preview`), { method: 'POST', body: JSON.stringify(input) });
  }

  createWorkerTransition(workerId: string, input: WorkerTransitionInput, idempotencyKey = crypto.randomUUID()): Promise<WorkerLifecycleChange> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workers/${encodeURIComponent(workerId)}/relationshipTransitions`), { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input) });
  }

  getWorkerLifecycle(eventId: string): Promise<WorkerLifecycleChange> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workerLifecycleEvents/${encodeURIComponent(eventId)}`));
  }

  getWorkerLifecycleDraft(eventId: string): Promise<WorkerLifecycleDraft> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workerLifecycleEvents/${encodeURIComponent(eventId)}/draft`));
  }

  updateWorkerLifecycleDraft(eventId: string, input: WorkerLifecycleDraftUpdate, idempotencyKey: string): Promise<WorkerLifecycleDraft> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workerLifecycleEvents/${encodeURIComponent(eventId)}/draft`), { method: 'PUT', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input) });
  }

  previewWorkerLifecycleDraft(eventId: string, input: WorkerLifecycleDraftUpdate): Promise<LifecyclePreview> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workerLifecycleEvents/${encodeURIComponent(eventId)}/draft/preview`), { method: 'POST', body: JSON.stringify(input) });
  }

  createWorkerLifecycle(workerId: string, processType: Exclude<WorkerLifecycleType, 'HIRE'>, input: WorkerLifecycleInput, idempotencyKey: string = crypto.randomUUID()): Promise<WorkerLifecycleChange> {
    const segment: Record<Exclude<WorkerLifecycleType, 'HIRE'>, string> = {
      LEAVE_OF_ABSENCE: 'leaveOfAbsence', RETURN_FROM_LEAVE: 'returnFromLeave', END_WORKER: 'endWorker',
    };
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workers/${encodeURIComponent(workerId)}/${segment[processType]}`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input),
    });
  }

  createPerson(input: CreatePersonInput, idempotencyKey = crypto.randomUUID()): Promise<CreatedPerson> {
    return this.request(this.tenantPath('/api/hr/v1/{tenant}/people'), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input),
    });
  }

  listPeopleReferences(): Promise<CollectionResponse<WorkdayReference>> {
    return this.requestAll((offset) => this.tenantPath(`/api/person/v4/{tenant}/people?limit=100&offset=${offset}`));
  }

  listHireCompanies(): Promise<CollectionResponse<HireReference>> {
    return this.request(this.tenantPath('/api/hr/v1/{tenant}/references/companies'));
  }

  listHireLocations(asOf = ''): Promise<CollectionResponse<HireReference>> {
    const query = new URLSearchParams();
    if (asOf) query.set('asOf', asOf);
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/references/locations${query.size ? `?${query}` : ''}`));
  }

  listHireJobProfiles(asOf = ''): Promise<CollectionResponse<HireReference>> {
    const query = new URLSearchParams();
    if (asOf) query.set('asOf', asOf);
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/references/jobProfiles${query.size ? `?${query}` : ''}`));
  }

  listHireJobLevels(asOf = ''): Promise<CollectionResponse<HireReference>> {
    const query = new URLSearchParams();
    if (asOf) query.set('asOf', asOf);
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/references/jobLevels${query.size ? `?${query}` : ''}`));
  }

  listHireJobTypes(workerType: 'EMPLOYEE' | 'CONTINGENT_WORKER', asOf = ''): Promise<CollectionResponse<HireReference>> {
    const query = new URLSearchParams({ workerType });
    if (asOf) query.set('asOf', asOf);
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/references/jobTypes?${query}`));
  }

  listHireWorkSpaces(locationId: string, asOf = ''): Promise<CollectionResponse<HireReference>> {
    const query = new URLSearchParams({ locationId });
    if (asOf) query.set('asOf', asOf);
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/references/workSpaces?${query}`));
  }

  createHire(personId: string, input: WorkerLifecycleInput, idempotencyKey = crypto.randomUUID()): Promise<WorkerLifecycleChange> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/people/${encodeURIComponent(personId)}/hire`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input),
    });
  }

  submitWorkerLifecycle(eventId: string, idempotencyKey: string = crypto.randomUUID(), expectedVersion?: number): Promise<WorkerLifecycleChange> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workerLifecycleEvents/${encodeURIComponent(eventId)}/submit`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(expectedVersion === undefined ? {} : { expectedVersion }),
    });
  }

  listStaffingChanges(processType = '', status = '', effectiveFrom = ''): Promise<CollectionResponse<StaffingChangeSummary>> {
    return this.requestAll((offset) => {
      const query = new URLSearchParams({ limit: '100', offset: String(offset) });
      if (processType) query.set('processType', processType);
      if (status) query.set('status', status);
      if (effectiveFrom) query.set('effectiveFrom', effectiveFrom);
      return this.tenantPath(`/api/hr/v1/{tenant}/staffingChanges?${query}`);
    });
  }

  correctWorkerLifecycle(eventId: string, input: WorkerLifecycleCorrectionInput, idempotencyKey = crypto.randomUUID()): Promise<WorkerLifecycleChange> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workerLifecycleEvents/${encodeURIComponent(eventId)}/correct`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input),
    });
  }

  getBusinessProcessEvent(id: string): Promise<BusinessProcessEvent> {
    return this.request(this.tenantPath(`/api/businessProcess/v1/{tenant}/events/${encodeURIComponent(id)}`));
  }

  listInProgressSteps(eventId: string): Promise<CollectionResponse<BusinessProcessStep>> {
    return this.request(this.tenantPath(`/api/businessProcess/v1/{tenant}/events/${encodeURIComponent(eventId)}/inProgressSteps`));
  }

  listInbox(): Promise<CollectionResponse<InboxItem>> {
    return this.requestAll<InboxItem>((offset) => this.tenantPath(`/api/hr/v1/{tenant}/inbox?limit=100&offset=${offset}`), 100, (item) => item.stepId);
  }

  approveBusinessProcessStep(stepId: string, comment = '', idempotencyKey: string = crypto.randomUUID()): Promise<BusinessProcessStep> {
    return this.request(this.tenantPath(`/api/businessProcess/v1/{tenant}/eventSteps/${encodeURIComponent(stepId)}/approve`), {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ comment }),
    });
  }

  denyBusinessProcessStep(stepId: string, comment = '', idempotencyKey: string = crypto.randomUUID()): Promise<BusinessProcessStep> {
    return this.request(this.tenantPath(`/api/businessProcess/v1/{tenant}/eventSteps/${encodeURIComponent(stepId)}/deny`), {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ comment }),
    });
  }

  cancelBusinessProcessEvent(eventId: string, comment = '', idempotencyKey: string = crypto.randomUUID()): Promise<BusinessProcessEvent> {
    return this.request(this.tenantPath(`/api/businessProcess/v1/{tenant}/events/${encodeURIComponent(eventId)}/cancel`), {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ comment }),
    });
  }

  listRequestableTimeOffTypes(workerId: string, asOf?: string): Promise<CollectionResponse<RequestableTimeOffType>> {
    return this.requestAll((offset) => this.tenantPath(`/api/absenceManagement/v5/{tenant}/workers/${encodeURIComponent(workerId)}/requestableTimeOffTypes?limit=100&offset=${offset}${asOf ? `&asOf=${encodeURIComponent(asOf)}` : ''}`));
  }

  listTimeOffTypes(): Promise<CollectionResponse<TimeOffType>> {
    return this.requestAll((offset) => this.tenantPath(`/api/absenceManagement/v5/{tenant}/timeOffTypes?limit=100&offset=${offset}`));
  }

  createTimeOffType(input: TimeOffTypeCreateRequest): Promise<TimeOffType> {
    return this.request(this.tenantPath('/api/absenceManagement/v5/{tenant}/timeOffTypes'), {
      method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() }, body: JSON.stringify(input),
    });
  }

  updateTimeOffType(id: string, input: Record<string, unknown>): Promise<TimeOffType> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/timeOffTypes/${encodeURIComponent(id)}`), {
      method: 'PATCH', headers: { 'Idempotency-Key': crypto.randomUUID() }, body: JSON.stringify(input),
    });
  }

  listTimeOffEntries(workerId: string): Promise<CollectionResponse<TimeOffEntry>> {
    return this.requestAll((offset) => this.tenantPath(`/api/absenceManagement/v5/{tenant}/workers/${encodeURIComponent(workerId)}/timeOffEntries?limit=100&offset=${offset}`));
  }

  getTimeOffEntry(entryId: string): Promise<TimeOffEntry> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/timeOffEntries/${encodeURIComponent(entryId)}`));
  }

  listTimeOffCalendar(input: TimeOffCalendarQuery): Promise<TimeOffCalendarResult> {
    const query = new URLSearchParams({ from: input.from, to: input.to, search: input.search, limit: String(input.limit), offset: String(input.offset) });
    if (input.view) query.set('view', input.view);
    if (input.organizationId) query.set('organizationId', input.organizationId);
    input.organizationIds?.forEach((id) => query.append('organizationIds', id));
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/timeOffCalendar?${query}`));
  }

  createTimeOffRequest(workerId: string, input: TimeOffRequestInput, idempotencyKey: string = crypto.randomUUID()): Promise<TimeOffRequest> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/workers/${encodeURIComponent(workerId)}/requestTimeOff`), {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(input),
    });
  }

  calculateTimeOffRequest(workerId: string, input: TimeOffRequestInput): Promise<TimeOffRequestCalculation> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/workers/${encodeURIComponent(workerId)}/timeOffCalculation`), {
      method: 'POST', body: JSON.stringify(input),
    });
  }

  listTimeOffRequests(workerId: string): Promise<CollectionResponse<TimeOffRequest>> {
    return this.requestAll((offset) => this.tenantPath(`/api/absenceManagement/v5/{tenant}/workers/${encodeURIComponent(workerId)}/timeOffRequests?limit=100&offset=${offset}`));
  }

  getTimeOffRequest(requestId: string): Promise<TimeOffRequest> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/timeOffRequests/${encodeURIComponent(requestId)}`));
  }

  submitTimeOffRequest(requestId: string, idempotencyKey: string = crypto.randomUUID()): Promise<TimeOffRequest> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/timeOffRequests/${encodeURIComponent(requestId)}/submit`), {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: '{}',
    });
  }

  cancelTimeOffRequest(requestId: string, comment: string, idempotencyKey: string = crypto.randomUUID()): Promise<unknown> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/timeOffRequests/${encodeURIComponent(requestId)}/cancel`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({ comment }),
    });
  }

  createDirectTimeOffEntry(workerId: string, input: TimeOffRequestInput, idempotencyKey: string = crypto.randomUUID()): Promise<TimeOffEntry> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/workers/${encodeURIComponent(workerId)}/timeOffEntries`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input),
    });
  }

  correctTimeOffEntry(entryId: string, input: TimeOffRequestInput, idempotencyKey: string = crypto.randomUUID()): Promise<TimeOffEntry> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/timeOffEntries/${encodeURIComponent(entryId)}/correct`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input),
    });
  }

  cancelTimeOffEntry(entryId: string, reason: string, idempotencyKey: string = crypto.randomUUID()): Promise<TimeOffEntry> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/timeOffEntries/${encodeURIComponent(entryId)}/cancel`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({ reason }),
    });
  }

  listDocuments(subject?: { subjectType: 'WORKER' | 'PERSON'; subjectId: string }): Promise<CollectionResponse<HRDocument>> {
    const filter = subject ? `&${new URLSearchParams(subject)}` : '';
    return this.requestAll((offset) => this.tenantPath(`/api/hr/v1/{tenant}/documents?limit=100&offset=${offset}${filter}`));
  }

  getDocument(id: string): Promise<HRDocument> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/documents/${encodeURIComponent(id)}`));
  }

  uploadDocument(input: UploadDocumentInput): Promise<HRDocument> {
    const body = new FormData();
    body.set('subjectType', input.subjectType);
    body.set('subjectId', input.subjectId);
    body.set('documentType', input.documentType);
    if (input.retentionUntil) body.set('retentionUntil', input.retentionUntil);
    body.set('file', input.file);
    return this.request(this.tenantPath('/api/hr/v1/{tenant}/documents'), { method: 'POST', body });
  }

  async downloadDocument(id: string): Promise<Blob> {
    const response = await this.fetchResponse(this.tenantPath(`/api/hr/v1/{tenant}/documents/${encodeURIComponent(id)}/download`));
    if (!response.ok) return parseResponse<Blob>(response);
    return response.blob();
  }

  getDashboardSummary(asOf?: string): Promise<DashboardSummary> {
    const query = new URLSearchParams();
    if (asOf) query.set('asOf', asOf);
    const suffix = query.size ? `?${query}` : '';
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/dashboard/summary${suffix}`));
  }

  async downloadWorkerReport(asOf?: string, workerIds?: string[]): Promise<Blob> {
    const query = new URLSearchParams();
    if (asOf) query.set('asOf', asOf);
    const suffix = query.size ? `?${query}` : '';
    const response = await this.fetchResponse(this.tenantPath(`/api/hr/v1/{tenant}/reports/workers.csv${suffix}`), {
      method: workerIds ? 'POST' : 'GET',
      body: workerIds ? JSON.stringify({ workerIds }) : undefined,
      headers: { Accept: 'text/csv' },
    });
    if (!response.ok) return parseResponse<Blob>(response);
    return response.blob();
  }

  listChecklists(workerId = '', status = ''): Promise<CollectionResponse<Checklist>> {
    return this.requestAll((offset) => {
      const query = new URLSearchParams({ limit: '100', offset: String(offset) });
      if (workerId) query.set('workerId', workerId);
      if (status) query.set('status', status);
      return this.tenantPath(`/api/hr/v1/{tenant}/checklists?${query}`);
    });
  }

  createChecklist(workerId: string, templateCode: string, targetOn: string): Promise<Checklist> {
    return this.request(this.tenantPath('/api/hr/v1/{tenant}/checklists'), {
      method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() }, body: JSON.stringify({ workerId, templateCode, targetOn }),
    });
  }

  listChecklistTemplates(): Promise<CollectionResponse<ChecklistTemplate>> {
    return this.request(this.tenantPath('/api/hr/v1/{tenant}/checklistTemplates'));
  }

  createChecklistTemplate(input: ChecklistTemplateCreateInput, idempotencyKey: string): Promise<ChecklistTemplate> {
    return this.request(this.tenantPath('/api/hr/v1/{tenant}/checklistTemplates'), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input),
    });
  }

  createChecklistTemplateVersion(templateId: string, expectedVersion: number, input: ChecklistTemplateVersionInput, idempotencyKey: string): Promise<ChecklistTemplate> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/checklistTemplates/${encodeURIComponent(templateId)}/versions`), {
      method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({ ...input, expectedVersion }),
    });
  }

  setChecklistTemplateActive(templateId: string, active: boolean, expectedVersion: number): Promise<ChecklistTemplate> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/checklistTemplates/${encodeURIComponent(templateId)}/activation`), {
      method: 'PUT', body: JSON.stringify({ active, expectedVersion }),
    });
  }

  completeChecklistItem(checklistId: string, itemId: string, note = ''): Promise<Checklist> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/checklists/${encodeURIComponent(checklistId)}/items/${encodeURIComponent(itemId)}/complete`), {
      method: 'POST', body: JSON.stringify({ note }),
    });
  }

  listHolidayCalendars(): Promise<CollectionResponse<HolidayCalendar>> {
    return this.requestAll((offset) => this.tenantPath(`/api/absenceManagement/v5/{tenant}/holidayCalendars?limit=100&offset=${offset}`));
  }

  createHolidayCalendar(input: HolidayCalendarCreateRequest): Promise<HolidayCalendar> {
    return this.request(this.tenantPath('/api/absenceManagement/v5/{tenant}/holidayCalendars'), { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() }, body: JSON.stringify(input) });
  }

  listHolidayDays(calendarId: string): Promise<CollectionResponse<{ id: string; date: string; descriptor: string; dayFraction: string }>> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/holidayCalendars/${encodeURIComponent(calendarId)}/days?limit=100&offset=0`));
  }

  createHolidayDay(calendarId: string, input: Record<string, unknown>): Promise<{ id: string; date: string; descriptor: string; dayFraction: string }> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/holidayCalendars/${encodeURIComponent(calendarId)}/days`), { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() }, body: JSON.stringify(input) });
  }

  listTimeOffPlans(): Promise<CollectionResponse<TimeOffPlan>> {
    return this.requestAll((offset) => this.tenantPath(`/api/absenceManagement/v5/{tenant}/timeOffPlans?limit=100&offset=${offset}`));
  }

  createTimeOffPlan(input: TimeOffPlanCreateRequest): Promise<TimeOffPlan> {
    return this.request(this.tenantPath('/api/absenceManagement/v5/{tenant}/timeOffPlans'), { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() }, body: JSON.stringify(input) });
  }

  createTimeOffPlanVersion(planId: string, input: Record<string, unknown>): Promise<TimeOffPlan> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/timeOffPlans/${encodeURIComponent(planId)}/versions`), { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() }, body: JSON.stringify(input) });
  }

  listTimeOffPlanAssignments(planId: string): Promise<CollectionResponse<TimeOffPlanAssignment>> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/timeOffPlans/${encodeURIComponent(planId)}/assignments?limit=100&offset=0`));
  }

  createTimeOffPlanAssignment(planId: string, input: Record<string, unknown>): Promise<TimeOffPlanAssignment> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/timeOffPlans/${encodeURIComponent(planId)}/assignments`), { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() }, body: JSON.stringify(input) });
  }

  listTimeOffGrantRules(planId: string): Promise<CollectionResponse<TimeOffGrantRule>> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/timeOffPlans/${encodeURIComponent(planId)}/grantRules?limit=100&offset=0`));
  }

  createTimeOffGrantRule(planId: string, input: TimeOffGrantRuleCreateRequest): Promise<TimeOffGrantRule> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/timeOffPlans/${encodeURIComponent(planId)}/grantRules`), { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() }, body: JSON.stringify(input) });
  }

  listTimeOffBalances(workerId: string, asOf?: string): Promise<CollectionResponse<TimeOffBalance>> {
    const query = asOf ? `?asOf=${encodeURIComponent(asOf)}` : '';
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/workers/${encodeURIComponent(workerId)}/timeOffBalances${query}`));
  }

  changeTimeOffBalance(workerId: string, entitlementId: string, operation: 'GRANT' | 'ADJUSTMENT', input: { quantity: string; effectiveOn: string; reason: string }, idempotencyKey: string = crypto.randomUUID()): Promise<EntitlementTransaction> {
    return this.request(this.tenantPath(`/api/absenceManagement/v5/{tenant}/workers/${encodeURIComponent(workerId)}/timeOffEntitlements/${encodeURIComponent(entitlementId)}/${operation}`), { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input) });
  }

  listEntitlementTransactions(workerId: string, entitlementId: string): Promise<CollectionResponse<EntitlementTransaction>> {
    return this.requestAll((offset) => this.tenantPath(`/api/absenceManagement/v5/{tenant}/workers/${encodeURIComponent(workerId)}/timeOffEntitlements/${encodeURIComponent(entitlementId)}/transactions?limit=100&offset=${offset}`));
  }

  runAbsenceMaintenance(asOf: string): Promise<AbsenceMaintenanceRun> {
    return this.request(this.tenantPath('/api/absenceManagement/v5/{tenant}/maintenanceRuns'), { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID() }, body: JSON.stringify({ asOf }) });
  }

  getWorkerRewards(workerId: string, asOf?: string): Promise<WorkerRewards> {
    const query = asOf ? `?asOf=${encodeURIComponent(asOf)}` : '';
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/workers/${encodeURIComponent(workerId)}/rewards${query}`));
  }

  createRewardChange(input: Record<string, unknown>, idempotencyKey: string = crypto.randomUUID()): Promise<RewardChange> {
    return this.request(this.tenantPath('/api/hr/v1/{tenant}/rewardChanges'), { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(input) });
  }

  submitRewardChange(changeId: string, idempotencyKey: string = crypto.randomUUID()): Promise<RewardChange> {
    return this.request(this.tenantPath(`/api/hr/v1/{tenant}/rewardChanges/${encodeURIComponent(changeId)}/submit`), { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: '{}' });
  }
}
